package fr.frux.core.network.api

import dev.zacsweers.metro.ContributesBinding
import dev.zacsweers.metro.Inject
import dev.zacsweers.metro.SingleIn
import fr.frux.core.common.di.AppScope
import fr.frux.core.network.ApiPaths
import fr.frux.shared.dto.GooglePlayPurchaseRequest
import fr.frux.shared.dto.PremiumStateDto
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.HttpStatusCode

/** Issue de la vérification serveur d'un achat. Le client ne décide jamais seul : il relit `auth/me`. */
sealed interface PurchaseVerification {
    /** 200 : droit écrit, prolongé, ou compte sans accès (abonnement expiré). [state] dit lequel. */
    data class Verified(
        val state: PremiumStateDto,
    ) : PurchaseVerification

    /** 202 : paiement différé, rien n'est écrit ni acquitté. À re-poster plus tard. */
    data class Pending(
        val state: PremiumStateDto,
    ) : PurchaseVerification
}

/**
 * Achat de Frux+ en store (#457, ADR 0033). Le client transmet le jeton d'achat Google Play et rien
 * d'autre, sans jamais acquitter lui-même. Les refus passent en exceptions du client HTTP :
 * 400 `INVALID_PURCHASE_TOKEN` ou `UNKNOWN_PRODUCT`, 403 `PURCHASE_ACCOUNT_MISMATCH`, 409
 * `PURCHASE_ALREADY_LINKED` (`ClientRequestException`), 503 `PLAY_BILLING_UNAVAILABLE`
 * (`ServerResponseException`, à réessayer au prochain lancement). POST jamais rejoué par le client
 * (`HttpRequestRetry` limité à GET/HEAD) : le re-POST explicite est idempotent côté serveur.
 */
interface PremiumApi {
    suspend fun verifyGooglePlayPurchase(purchaseToken: String): PurchaseVerification
}

@ContributesBinding(AppScope::class)
@SingleIn(AppScope::class)
@Inject
class PremiumApiImpl(
    private val httpClient: HttpClient,
) : PremiumApi {
    override suspend fun verifyGooglePlayPurchase(purchaseToken: String): PurchaseVerification {
        val response =
            httpClient.post("${ApiPaths.V1}/premium/google") {
                setBody(GooglePlayPurchaseRequest(purchaseToken))
            }
        val state: PremiumStateDto = response.body()
        return if (response.status == HttpStatusCode.Accepted) {
            PurchaseVerification.Pending(state)
        } else {
            PurchaseVerification.Verified(state)
        }
    }
}
